/**
 * Strip 7 jours : un bouton par jour avec icône dominante + min/max température.
 *
 * Au clic, le curseur d'exploration saute sur l'heure de midi du jour cliqué
 * (cible "représentative" — fallback sur la première heure si midi absent).
 * Le bouton du jour pointé par le curseur est mis en relief (border foncée).
 */

import { useMemo } from "react";

import { getWeatherMeta } from "./formatters.js";
import type { JourMeteo, MeteoPoint } from "./types.js";

export function StripJours({
  parJour,
  hourly,
  indexCurseur,
  indexMaintenant,
  onClickJour,
}: {
  parJour: JourMeteo[];
  hourly: MeteoPoint[];
  indexCurseur: number;
  indexMaintenant: number;
  onClickJour: (index: number) => void;
}) {
  // Pré-calcule l'index "midi" de chaque jour (heure la plus représentative
  // pour un clic) en une passe. Évite de scanner `hourly` pour chaque carte.
  const indexParJour = useMemo(() => {
    const result: Array<{ first: number; midi: number }> = [];
    let dernierJour = "";
    for (let i = 0; i < hourly.length; i++) {
      const p = hourly[i];
      if (!p) continue;
      const jourStr = p.time.slice(0, 10);
      if (jourStr !== dernierJour) {
        result.push({ first: i, midi: i });
        dernierJour = jourStr;
      }
      // Heure la plus proche de 12h → cible "représentative" du jour.
      if (p.time.endsWith("T12:00")) {
        const last = result[result.length - 1];
        if (last) last.midi = i;
      }
    }
    return result;
  }, [hourly]);

  const jourCurseur = hourly[indexCurseur]?.time.slice(0, 10);

  return (
    <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
      {parJour.map((j, i) => {
        const meta = getWeatherMeta(j.iconCode);
        const Icone = meta.icon;
        const cibleIndex = indexParJour[i]?.midi ?? indexParJour[i]?.first ?? indexMaintenant;
        const dateBucket = hourly[cibleIndex]?.time.slice(0, 10);
        const estCurseur = dateBucket === jourCurseur;
        return (
          <button
            key={`${j.libelle}-${i}`}
            type="button"
            onClick={() => onClickJour(cibleIndex)}
            // Taille cible tactile suffisante pour un doigt en condition terrain.
            // Le bouton est plein-bloc pour rester scannable rapidement.
            className={`flex min-w-[78px] flex-1 items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors ${
              estCurseur
                ? "border-champagne-700 bg-champagne-100"
                : j.estAujourdhui
                  ? "border-champagne-300 bg-champagne-50 hover:bg-champagne-100"
                  : "border-champagne-200/70 bg-white hover:bg-champagne-50"
            }`}
            aria-pressed={estCurseur}
          >
            <Icone aria-hidden className="h-4 w-4 shrink-0" stroke={meta.color} strokeWidth={1.7} />
            <div className="leading-tight">
              <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-champagne-700">
                {j.libelle}
              </div>
              <div className="mono tnum text-[12px] text-champagne-900">
                <span className="font-semibold">{j.max}°</span>
                <span className="text-champagne-700">{" / "}</span>
                <span>{j.min}°</span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
